import type { AppDefinition } from '@/types/app';
import type { ServerEdit } from './buildClient';
import { subjectDrift } from './drift';
import { lastVersion, popVersion } from './editHistory';
import { humanize } from './readable';
import {
  addedLists,
  changedListRows,
  listRowsHeadline,
  liveRowsNotice,
  newListsNotice,
  PHONE_LIVE_PLACE,
  sameApartFromListRows,
} from './listEdits';

/**
 * Changing a published web app from a sentence — the loop the product advertises.
 *
 * The editor's own seeded message says "tell me what to change and I'll update it", and
 * until now chat could not edit a web app at all. Worse than could not: it asked the model
 * for a brand-new ENGINE app and wrote the result over the record, so `source`, `runtime`,
 * `webUrl` and `hostedAppId` were destroyed and the app could never be republished again.
 * That was stopped, and people were sent to the Content tab instead. This restores the
 * loop properly, against the server's `/edit` route, which has existed the whole time with
 * nothing calling it.
 *
 * The ORDER is the safety property, the same one account deletion and content editing
 * each had to learn: the new source is written down BEFORE the live page is touched.
 * A publish that fails then costs somebody a page that is briefly out of date, which is
 * visible and recoverable. The other order costs them the edit, which is neither.
 */
export type WebEditOutcome =
  /**
   * Source saved and the page's document replaced. `offline` when the page is not being
   * served — taken offline by its owner — so the edit reaches nobody until it is put back
   * online, and saying "your live page is updated" would be untrue.
   */
  | {
    status: 'updated';
    source: string;
    note?: string;
    offline?: true;
    /**
     * The S.list lists whose STARTING rows this edit changed — none of which changed on the page
     * (`changedListRows`). Carried as a fact rather than folded into `note`, because it decides
     * the HEADLINE: see `editReply`.
     */
    listsUnchanged?: string[];
    /** Those rows were ALL the edit changed, so nothing on the page did (`sameApartFromListRows`). */
    onlyListRows?: true;
    /** Where the reply says those rows are edited, as the client that ran the edit names it. */
    livePlace?: string;
  }
  /** Source saved; the page is behind. Retrying publishes it — the work is not lost. */
  | { status: 'saved_not_live'; source: string; message: string }
  /**
   * Nothing changed. `reason` is the server's refusal code (`insufficient_credits`,
   * `signup_required`, …) when the EDIT REQUEST itself was refused — see `runWebAppEdit`.
   */
  | { status: 'failed'; message: string; reason?: string };

export interface WebAppEditDeps {
  /** The app as it stands. Refused if empty — there is nothing to change. */
  source: string;
  instruction: string;
  /** What the app was originally for, so an edit reads as an edit and not a fresh build. */
  goal?: string;
  edit: (input: { source: string; instruction: string; goal?: string }) => Promise<ServerEdit>;
  /** Persist the new source locally. Must happen before the page is touched. */
  save: (source: string) => Promise<void>;
  /**
   * Push the saved source to the page. Answers `{ offline: true }` when the page it updated
   * is not being served, because a republish no longer switches a page back on.
   */
  publish: (source: string) => Promise<void | { offline?: boolean }>;
  /**
   * What the owner is told to do when the page could not be updated, after "Your change is
   * saved" is true and the page is behind.
   *
   * Each client names its OWN control. The phone's is the ⋯ menu's Update website, which it
   * offers exactly while the page is behind (`livePageBehind`); the web builder's is its
   * Publish changes button. A sentence written here for both named neither, and on the phone
   * it sent people to publish from a Preview card and a Content button that no longer exist.
   * Absent keeps the generic words the web builder has always shown.
   */
  publishAgain?: string;
  /**
   * Where the owner edits a list's live rows, as this client names it: the phone's "Manage →
   * Live" (the default) or the web's "Your data → Live" (`WEB_LIVE_PLACE`). The web's replies sent
   * owners to Manage, which the web does not have (F10, deep-dive 2026-09-30).
   */
  livePlace?: string;
}

/**
 * What an owner is told when an edit reached a page that is switched off.
 *
 * Saving is true and so is "updated", but "your live page is updated" is not: nobody can see
 * it. Named here, beside the outcome that carries `offline`, so chat and the editor's own
 * Update website say the same thing and point at the same place to switch it back on.
 */
export const SAVED_BUT_OFFLINE =
  "Saved. Your page is offline, so visitors won't see this until you put it back online from Your website.";

/** Said when an edit is on the page people open. */
export const LIVE_PAGE_UPDATED = 'Done — your live page is updated.';

/** The phone's `publishAgain`: where Update website is, which is where the page is put right. */
export const PUBLISH_AGAIN_FROM_MENU =
  'Your change is saved. When you have a connection, tap ⋯ and choose Update website to put it live.';

/**
 * Said when the same change is still being made on the server — the dropped-response retry.
 *
 * The server's own words for this are about a BUILD ("That build is still running…"), and an
 * owner who resent a sentence about their golf app was told a build was running. What is true
 * of an edit: it is still being made, waiting is the fix, and sending it again will not charge
 * twice (the retry carries the same request key, so the server answers it from the first).
 */
export const EDIT_STILL_RUNNING =
  'That change is still being made. Give it a minute, then send it again — you will not be charged twice.';

/**
 * An edit that came back as the app it was sent — nothing to save and nothing to publish.
 *
 * It used to be saved and published like any other and answered "Done — your live page is
 * updated", which told the owner something had changed when nothing had. Said as a failure so
 * each client keeps what was typed, ready to be put another way.
 *
 * It also says the edit was paid for, because it was: the server charges an edit before the model
 * runs and refunds only a run that FAILS, and this one succeeded at returning the same app. Every
 * sibling sentence says whether anything was charged, and a person about to try again should
 * know that trying again costs another edit.
 */
export const EDIT_CHANGED_NOTHING =
  'That came back without any change, so your app is exactly as it was — but it still used an edit’s credits. Try saying it another way.';

function reason(e: unknown, fallback: string): string {
  const m = e instanceof Error ? e.message.trim() : '';
  return m || fallback;
}

/**
 * The saved source, put on the live page — the half an edit and an undo share.
 *
 * `note` rides on the failure too: what is SAVED may be what the owner needs warning about, and
 * a failed publish is exactly when the next retry will put it live.
 */
async function putLive(
  deps: Pick<WebAppEditDeps, 'publish' | 'publishAgain'>,
  source: string,
  note: string | undefined,
): Promise<{ live: true; offline: boolean } | { live: false; message: string }> {
  try {
    const answer = (await deps.publish(source)) as { offline?: boolean } | undefined;
    return { live: true, offline: answer?.offline === true };
  } catch (e) {
    /*
     * A refusal about the app itself — taken down, made private, too large to host — is not a
     * connection problem, and "publish again when you have a connection" would send somebody
     * round in a circle. The server's own sentence says what can help. See `HostRefusedError`.
     */
    const refused = (e as { refused?: unknown } | null)?.refused === true;
    return {
      live: false,
      message: [
        refused
          ? `Saved, but not live: ${reason(e, 'the server would not publish this page.')}`
          : `${reason(e, 'The live page could not be updated.')} ${deps.publishAgain ?? 'Your change is saved — publish again when you have a connection.'}`,
        note,
      ].filter(Boolean).join('\n\n'),
    };
  }
}

export async function runWebAppEdit(deps: WebAppEditDeps): Promise<WebEditOutcome> {
  if (!deps.source.trim()) {
    return { status: 'failed', message: 'There is no app to change yet.' };
  }

  let result: ServerEdit;
  try {
    result = await deps.edit({ source: deps.source, instruction: deps.instruction, goal: deps.goal });
  } catch (e) {
    /*
     * The server's refusal code is carried, and ONLY from here.
     *
     * Keeping just the message turned an out-of-credits edit into "Could not generate — You
     * are out of credits." with a single OK: the editor routes a denial to the paywall or to
     * sign-up by its REASON, and the reason was dropped on the floor. Chat is the one live
     * edit path, so the person most likely to buy credits was the one never shown where.
     *
     * Not from `save` or `publish` below: those fail on this device or on the way to the
     * page, and a local write that failed must never send somebody to a paywall.
     */
    const code = (e as { reason?: unknown } | null)?.reason;
    return {
      status: 'failed',
      message: code === 'build_in_progress' ? EDIT_STILL_RUNNING : reason(e, 'That change could not be made. Try again.'),
      ...(typeof code === 'string' ? { reason: code } : {}),
    };
  }

  const next = (result?.appCode ?? '').trim();
  if (!next) {
    // An empty body would blank the app if it were saved. Refuse rather than write it.
    return { status: 'failed', message: 'That change came back empty, so nothing was altered.' };
  }
  if (next === deps.source.trim()) return { status: 'failed', message: EDIT_CHANGED_NOTHING };

  try {
    await deps.save(next);
  } catch (e) {
    return { status: 'failed', message: reason(e, 'That change could not be saved.') };
  }

  /*
   * Carried, not swallowed. The server flags an edit that looks like it REPLACED the app —
   * "add a phone number field" coming back as a different product for a different person.
   * It reports rather than refuses because a sweeping instruction is legitimate, and only
   * the person who typed it knows which they meant. What must not happen is the swap going
   * out unremarked.
   */
  const drifted = result.drift?.reasons?.length
    ? `This looks like a bigger change than you asked for: ${result.drift.reasons.join('; ')}. If that is not what you wanted, tell me what to put back.`
    : undefined;

  /*
   * An edit to a live list's STARTING rows changes nothing a visitor sees — see `changedListRows`.
   * A café's "remove the Preview tasting event and the Loyalty card launch" (2026-09-29) was
   * answered "Done — your live page is updated" while both offers stayed on the page.
   *
   * Worked out HERE, from the two sources this already holds, rather than returned by /edit
   * beside `drift`. Both clients run this function, so it is still one function in one place.
   * It needs no server deploy to be said, and it is said for an answer the server replays from
   * a receipt written before any deploy. And a field the server added would have to survive
   * each client's own copy of the answer — the web's picks `appCode` and `drift` by name, and
   * would have dropped it without a word.
   *
   * Carried as `listsUnchanged` rather than in `note`, because it decides the headline. The first
   * version put it in the note, under "Done — your live page is updated.", and for the café's edit
   * the rows were the WHOLE edit: nothing on the page had changed, so the headline was false
   * (review, 2026-09-29). `editReply` says it in the headline's place.
   */
  const listsUnchanged = changedListRows(deps.source, next);

  /*
   * A list the edit ADDED starts empty on the page: nothing writes starting rows into an app that
   * already exists (`addedLists`). The first fix left it out, and the reply said "Done" alone over
   * a new section with nothing in it (review, 2026-09-29). In the note, before the drift warning,
   * for the reason the rows' sentence comes first: it is about the change the owner asked for.
   */
  const place = deps.livePlace ?? PHONE_LIVE_PLACE;
  const note = [newListsNotice(addedLists(deps.source, next), place), drifted].filter(Boolean).join('\n\n') || undefined;

  /*
   * The drift warning rides on a failed publish too. Chat prints `message` alone on that branch,
   * so a drifting edit that also failed to publish used to say nothing about the drift — in the
   * one case where it matters most, since what is now SAVED may be a different app and the next
   * retry will publish it. The live rows ride on it for the same reason, first: the retry will
   * not change them either.
   */
  const live = await putLive(deps, next, [liveRowsNotice(listsUnchanged, place), note].filter(Boolean).join('\n\n') || undefined);
  if (!live.live) return { status: 'saved_not_live', source: next, message: live.message };

  return {
    status: 'updated',
    source: next,
    ...(note ? { note } : {}),
    ...(live.offline ? { offline: true as const } : {}),
    ...(listsUnchanged.length
      ? {
        listsUnchanged,
        ...(sameApartFromListRows(deps.source, next) ? { onlyListRows: true as const } : {}),
        ...(deps.livePlace ? { livePlace: deps.livePlace } : {}),
      }
      : {}),
  };
}

/**
 * What chat says for an edit that was saved — on the phone and on the web, from one function.
 *
 * The headline is the claim, and it must be true of THIS edit. The café's "remove the Preview
 * tasting event and the Loyalty card launch" (2026-09-29) changed only an S.list's starting rows,
 * so nothing on the page changed: it is answered "Saved — but your offers list did not change on
 * your page…", never "Done". An edit that ALSO changed something else says the page is updated,
 * except that list. Each client used to write its own "Done — your live page is updated." over
 * `note`, which is how the first fix left the false headline standing on both.
 *
 * A page that is offline keeps its own headline, followed by which rows did not change: "visitors
 * won't see this until you put it back online" is true of the rest of the edit. Not when the rows
 * were ALL of it — then there is nothing for visitors to see later either, and the rows' headline
 * is the one that is true.
 */
export function editReply(outcome: Exclude<WebEditOutcome, { status: 'failed' }>): string {
  if (outcome.status === 'saved_not_live') return outcome.message;
  const lists = outcome.listsUnchanged ?? [];
  const place = outcome.livePlace ?? PHONE_LIVE_PLACE;
  const headline = !lists.length
    ? [outcome.offline ? SAVED_BUT_OFFLINE : LIVE_PAGE_UPDATED]
    : outcome.onlyListRows
      ? [listRowsHeadline(lists, true, place)]
      : outcome.offline
        ? [SAVED_BUT_OFFLINE, liveRowsNotice(lists, place)]
        : [listRowsHeadline(lists, false, place)];
  return [...headline, outcome.note].filter(Boolean).join('\n\n');
}

/* ------------------------------------------------------------------------------------------ *
 * Undo last change.
 *
 * The same loop run backwards: the source from before the last change is put back, written
 * down FIRST, then published as a content update (a page its owner took offline stays offline).
 * Shared by both clients for the same reasons the edit is — the order, and the words.
 * ------------------------------------------------------------------------------------------ */

/** The control's name, on the phone and on the web. */
export const UNDO_LAST_CHANGE = 'Undo last change';

/** Said when an undo reached the live page. */
export const UNDONE = 'Undone — your live page is back to how it was.';

/** Said when an undo reached a page that is switched off: saved, and nobody can see it yet. */
export const UNDONE_BUT_OFFLINE =
  "Undone. Your page is offline, so visitors won't see this until you put it back online from Your website.";

export const NOTHING_TO_UNDO = 'There is nothing to undo.';

/**
 * Said when the app changed between the undo being worked out and being written — an edit or a
 * Manage save landed meanwhile. Nothing is written: the undo never saw that change, and laying
 * the older source over it would throw it away. Undoing again takes back whatever is latest.
 */
export const UNDO_CHANGED_MEANWHILE =
  'Your app changed while that was being undone, so nothing was changed. Undo again to take back the latest change.';

/**
 * What a source change made in the Manage tab is called in the history.
 *
 * Manage rewrites the app's own source (its copy, its seeded rows), so it is a change like any
 * other, and it is recorded like one. If it were not, "Undo last change" after a Manage edit
 * would put back the source from before the CHAT edit and silently throw the Manage edit away.
 */
export const CHANGED_IN_MANAGE = 'a change made in Manage';

/**
 * What each Manage change is called when it is kept for Undo, on BOTH clients.
 *
 * The Undo confirmation reads it back — "…before “Change an item in Courses”". The phone's
 * Manage passed no words at all, so it said "before “a change made in Manage”" about the change
 * the web called "Change an item in courses": two sentences for one undo. A list change is
 * `manageListChange(what, list)`; a wording change is `manageTextChange(was, now)`.
 *
 * A field and a list are named as Manage labels them (`humanize`): once its headers read
 * "Courses" and "Starts at", Undo still said "Change an item in COURSES" and "Remove the
 * startsAt field" — the last place the owner met the code's name for a thing.
 */
export const MANAGE_CHANGE = {
  addItem: 'Add an item',
  changeItem: 'Change an item',
  moveItem: 'Move an item',
  deleteItem: 'Delete an item',
  addField: (field: string) => `Add the ${humanize(field)} field`,
  removeField: (field: string) => `Remove the ${humanize(field)} field`,
} as const;

/** A change to one of the page's lists, in words: `COURSES` → "Change an item in Courses". */
export function manageListChange(what: string, list: string): string {
  return `${what} in ${humanize(list)}`;
}

/** A change to the page's wording, in words: "Change Start a round to Tee off". */
export function manageTextChange(was: string, now: string): string {
  return `Change ${was} to ${now.trim()}`;
}

/**
 * The saved data an undo would leave behind — or undefined when it leaves none.
 *
 * The version being restored is older than the app now live, and people may have saved rows
 * into a collection the newer app added (a golf app's `clubShots`, say) or moved (`rounds` from
 * S.store to S.cloud). Going back strands those rows exactly as an edit that drops a collection
 * does, so it is checked with the same guard the edit route uses (`subjectDrift`), and the owner
 * is told in the same terms, with the same way back. Only the COLLECTIONS are read: the screens
 * and headings the undo removes are the change being undone, and warning about them would be
 * warning the owner about what they asked for.
 */
export function strandedByUndo(definition: AppDefinition): string | undefined {
  const version = lastVersion(definition);
  if (!version) return undefined;
  const { droppedCollections, movedCollections } = subjectDrift(definition.source ?? '', version.source);
  const lost: string[] = [];
  if (droppedCollections.length) {
    lost.push(`${droppedCollections.join(', ')} ${droppedCollections.length === 1 ? 'is' : 'are'} no longer in the app`);
  }
  if (movedCollections.length) {
    lost.push(movedCollections.map((m) => `${m.collection} is kept somewhere else (${m.from} → ${m.to})`).join(', '));
  }
  if (!lost.length) return undefined;
  return `Going back means ${lost.join(', and ')}, so anything saved there since that change is stranded (not deleted). If that is not what you wanted, tell me what to put back.`;
}

/**
 * What to ask before undoing — or null when there is nothing to undo.
 *
 * Asked, because it acts at once on a page other people may be using, and because the change it
 * throws away may have been paid for: there is no redo. It names the change being undone, which
 * also settles which one "last" means when the last one was made in Manage rather than in chat.
 */
export function undoConfirmation(definition: AppDefinition): { title: string; body: string; confirm: string } | null {
  const version = lastVersion(definition);
  if (!version) return null;
  const what = version.instruction.trim();
  const named = what.length > 90 ? `${what.slice(0, 87).trimEnd()}…` : what;
  return {
    title: `${UNDO_LAST_CHANGE}?`,
    body: [
      named
        ? `Your live page goes back to how it was before “${named}”.`
        : 'Your live page goes back to how it was before your last change.',
      strandedByUndo(definition),
    ].filter(Boolean).join('\n\n'),
    confirm: 'Undo',
  };
}

export type UndoOutcome =
  /** Restored and saved, and the page's document replaced. `offline` as for an edit. */
  | { status: 'undone'; source: string; note?: string; offline?: true }
  /** Restored and saved; the page is behind. Retrying the publish puts it live. */
  | { status: 'saved_not_live'; source: string; message: string }
  | { status: 'failed'; message: string };

export interface UndoDeps {
  /** The record as it is STORED now, read by id just before this runs. */
  definition: AppDefinition;
  /**
   * Write the restored record — the older source back, the version taken off. Before the page
   * is touched. Should refuse if the stored source has moved since `definition` was read: an
   * undo laid over a change it never saw would throw that change away.
   */
  save: (restored: AppDefinition) => Promise<void>;
  publish: WebAppEditDeps['publish'];
  publishAgain?: string;
}

export async function runUndo(deps: UndoDeps): Promise<UndoOutcome> {
  const { definition: restored, version } = popVersion(deps.definition);
  if (!version) return { status: 'failed', message: NOTHING_TO_UNDO };
  const note = strandedByUndo(deps.definition);
  const source = restored.source ?? '';

  try {
    await deps.save(restored);
  } catch (e) {
    return { status: 'failed', message: reason(e, 'That could not be undone.') };
  }

  const live = await putLive(deps, source, note);
  if (!live.live) return { status: 'saved_not_live', source, message: live.message };
  return { status: 'undone', source, ...(note ? { note } : {}), ...(live.offline ? { offline: true as const } : {}) };
}
